import {useEffect} from 'react';
import axios from 'axios';
import {Box, CircularProgress, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {
    useAppDispatch,
    useAppSelector,
    selectAppConnectionIssue,
    setAppConnectionIssueAction,
} from 'store';

const PROBE_INTERVAL = 15000;

/**
 * Check whether the API is reachable again. Plain axios, so it's not retried
 * and doesn't feed back into the connection state itself.
 * @return {Promise<boolean>}
 */
const probeApi = async () => {
    try {
        await axios.get('__healthcheck', {
            baseURL: process.env.REACT_APP_URL,
            timeout: 5000,
        });
        return true;
    } catch (error) {
        return false;
    }
};

/**
 * Shown while API requests can't reach the server. Background requests
 * fail silently, so this is the one place the user hears about it; it
 * clears itself on the next successful request or health check.
 */
export const ConnectionBanner = () => {
    const dispatch = useAppDispatch();
    const connectionIssue = useAppSelector(selectAppConnectionIssue);
    const {t} = useTranslation();

    useEffect(() => {
        const handleOffline = () =>
            dispatch(setAppConnectionIssueAction(true));
        const handleOnline = async () => {
            if (await probeApi()) dispatch(setAppConnectionIssueAction(false));
        };

        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);
        return () => {
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
        };
    }, [dispatch]);

    useEffect(() => {
        if (!connectionIssue) return;

        const interval = setInterval(async () => {
            if (!navigator.onLine) return;
            if (await probeApi()) dispatch(setAppConnectionIssueAction(false));
        }, PROBE_INTERVAL);
        return () => clearInterval(interval);
    }, [connectionIssue, dispatch]);

    if (!connectionIssue) return null;

    return (
        <Box
            role="status"
            aria-live="polite"
            sx={{
                position: 'fixed',
                top: 12,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 2000,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                maxWidth: 'calc(100% - 32px)',
                px: 2,
                py: 1,
                borderRadius: '20px',
                bgcolor: 'secondary.main',
                color: '#ffffff',
                boxShadow: '0px 4px 14px rgba(0, 0, 0, 0.25)',
            }}>
            <CircularProgress size={14} sx={{color: 'primary.main'}} />
            <Typography variant="body2">
                {t('axios.connectionProblemsReconnecting')}
            </Typography>
        </Box>
    );
};
